import { Loader2, Mic } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import React, { useEffect, useRef } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useVoiceInput } from '@apps/web/hooks/use_voice_input';
import { EVENTS } from '@apps/web/lib/events';
import { useMode } from '@apps/web/lib/mode_store';
import { type MessageRatingLabel } from '@libraries/typescript/api_client';

type MessageRatingTextareaProps = {
    readonly selectedLabel: MessageRatingLabel | null;
    readonly isSaving: boolean;
    readonly isOperationPending: boolean;
    readonly draftComment: string;
    readonly hasUnsavedComment: boolean;
    readonly onCommentChange: (
        event: React.ChangeEvent<HTMLTextAreaElement>,
    ) => void;
    readonly onSaveComment: () => Promise<boolean>;
    readonly disabled?: boolean;
    readonly organizationId?: string;
    readonly onSetDraftComment?: (text: string) => void;
};

export function MessageRatingTextarea({
    selectedLabel,
    isSaving,
    isOperationPending,
    draftComment,
    hasUnsavedComment,
    onCommentChange,
    onSaveComment,
    disabled = false,
    organizationId,
    onSetDraftComment,
}: MessageRatingTextareaProps) {
    const isDisabled = disabled || isSaving || isOperationPending;
    // Voice input deliberately ignores both `isSaving` and
    // `isOperationPending` — those reflect in-hook label/comment writes
    // that should run concurrently with voice recording rather than
    // abort it. Voice still respects the parent's `disabled` prop
    // (approve/reject in flight on the staged message).
    const isVoiceInputDisabled = disabled;
    const posthog = usePostHog();
    const mode = useMode();
    const draftIncludesVoiceInputRef = useRef(false);

    const voiceInputEnabled = !!organizationId && !!onSetDraftComment;

    const {
        canRecordAudio,
        isRecording,
        isTranscribing,
        voiceInputError,
        startRecording,
        stopRecording,
    } = useVoiceInput({
        organizationId: organizationId ?? '',
        currentText: draftComment,
        onTranscribed: (appendedText) => {
            draftIncludesVoiceInputRef.current = true;
            onSetDraftComment?.(appendedText);
        },
        composer: 'message_rating',
        voiceInputFlow: 'message_rating_feedback',
        disabled: !voiceInputEnabled || !selectedLabel || isVoiceInputDisabled,
    });

    useEffect(() => {
        if (!selectedLabel) {
            draftIncludesVoiceInputRef.current = false;
        }
    }, [selectedLabel]);

    const handleSaveComment = async () => {
        const includedVoiceInput = draftIncludesVoiceInputRef.current;
        const draftLength = draftComment.trim().length;
        const saveSucceeded = await onSaveComment();
        if (saveSucceeded && includedVoiceInput && organizationId) {
            posthog?.capture(EVENTS.VOICE_INPUT_FEEDBACK_SUBMITTED, {
                composer: 'message_rating',
                draft_length: draftLength,
                mode,
                organization_id: organizationId,
                voice_input_flow: 'message_rating_feedback',
            });
        }
        if (saveSucceeded) {
            draftIncludesVoiceInputRef.current = false;
        }
    };

    const showMicButton = voiceInputEnabled && canRecordAudio;

    return (
        <React.Fragment>
            {selectedLabel && (
                <div className="animate-in fade-in slide-in-from-top-1 w-full space-y-2 duration-200">
                    <div className="border-input rounded-lg border bg-transparent px-3 py-2">
                        <div className="flex items-center gap-2">
                            <Textarea
                                value={draftComment}
                                onChange={onCommentChange}
                                onBlur={() => {
                                    if (hasUnsavedComment) {
                                        void handleSaveComment();
                                    }
                                }}
                                placeholder={
                                    isRecording
                                        ? 'Listening…'
                                        : 'Add a comment about this message (optional)'
                                }
                                className="max-h-24 min-h-[1.5rem] flex-1 resize-none border-0 bg-transparent p-0 text-xs shadow-none focus-visible:ring-0"
                                disabled={
                                    isDisabled || isRecording || isTranscribing
                                }
                            />
                            {showMicButton ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size={isTranscribing ? 'sm' : 'icon'}
                                    className={
                                        isTranscribing
                                            ? 'h-7 flex-shrink-0 self-center rounded-full px-2 text-xs font-medium'
                                            : 'h-7 w-7 flex-shrink-0 self-center rounded-full'
                                    }
                                    aria-label={
                                        isTranscribing
                                            ? 'Transcribing voice message'
                                            : isRecording
                                              ? 'Stop recording'
                                              : 'Record voice message'
                                    }
                                    disabled={
                                        isTranscribing ||
                                        (!isRecording && isVoiceInputDisabled)
                                    }
                                    onClick={() => {
                                        if (isRecording) {
                                            stopRecording();
                                            return;
                                        }

                                        void startRecording();
                                    }}
                                >
                                    {isTranscribing ? (
                                        <span className="flex items-center gap-1">
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                            <span>Transcribing</span>
                                        </span>
                                    ) : (
                                        <span className="relative flex items-center justify-center">
                                            <Mic className="h-3 w-3" />
                                            {isRecording ? (
                                                <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                                            ) : null}
                                        </span>
                                    )}
                                </Button>
                            ) : null}
                            {isSaving ? (
                                <Loader2 className="text-muted-foreground h-3 w-3 flex-shrink-0 animate-spin self-center" />
                            ) : null}
                        </div>
                    </div>
                    {voiceInputError ? (
                        <p className="text-destructive text-xs">
                            {voiceInputError}
                        </p>
                    ) : null}
                </div>
            )}
        </React.Fragment>
    );
}
